import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Smartphone, Mail, Fingerprint, ChevronRight, Globe, HelpCircle, Home, Plane, GraduationCap, ChevronDown } from "lucide-react";
import { Logo } from "../components/Logo";
import { LANGUAGE_NAMES, errMsg } from "../lib/api";
import { setLanguage } from "../i18n";
import { getDevice } from "../components/Pin";
import { loginWithPasskey, passkeysSupported } from "../lib/webauthn";
import { useAuth } from "../context/AuthContext";

const IMG = {
  woman: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=900&q=80",
  family: "https://images.unsplash.com/photo-1609220136736-443140cffec6?w=500&q=80",
  travel: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=500&q=80",
  education: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=500&q=80",
};

const Option = ({ icon: Icon, label, onClick, primary, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full flex items-center gap-4 p-3 pr-5 rounded-2xl border transition-all hover:-translate-y-0.5 hover:shadow-soft active:scale-[0.99] ${primary ? "bg-nini-mint/70 border-nini-mint" : "bg-white border-nini-border"}`}>
    <span className={`h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full inline-flex items-center justify-center ${primary ? "bg-white text-nini-deep" : "bg-nini-surface text-nini-heading"}`}><Icon size={24} strokeWidth={1.8} /></span>
    <span className="flex-1 text-left text-base sm:text-lg font-semibold text-nini-heading">{label}</span>
    <ChevronRight size={20} className="text-nini-heading" />
  </button>
);

const Bubble = ({ src, icon: Icon, className, size = "h-44 w-44", testId }) => (
  <div className={`relative ${className}`} data-testid={testId}>
    <div className={`${size} rounded-full overflow-hidden ring-4 ring-white shadow-lift`}><img src={src} alt="" className="w-full h-full object-cover" /></div>
    <span className="absolute -right-2 bottom-2 h-16 w-16 rounded-full bg-white shadow-soft inline-flex items-center justify-center text-nini-deep"><span className="h-12 w-12 rounded-full bg-nini-mint inline-flex items-center justify-center"><Icon size={22} /></span></span>
  </div>
);

export default function Landing() {
  const nav = useNavigate(); const { login } = useAuth(); const { t } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem("nini_lang") || "en");
  const biometrics = async () => {
    try {
      if (!passkeysSupported()) throw new Error("This browser does not support fingerprint or Face ID sign-in.");
      const data = await loginWithPasskey(); login(data); nav("/app");
    } catch (e) {
      const dev = getDevice();
      if (dev) { toast("Fingerprint not available — unlock with your PIN instead"); return nav("/login?mode=pin"); }
      toast.error(e?.name === "NotAllowedError" ? "Biometric sign-in was cancelled or not set up on this device." : errMsg(e), { description: "Log in with email or phone, then add fingerprint / Face ID under Settings → Security." });
      nav("/login");
    }
  };
  return (
    <div className="min-h-screen relative overflow-hidden bg-white text-nini-text" data-testid="landing-page">
      <div className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full bg-nini-mint/70 blur-sm" />
      <div className="pointer-events-none absolute top-1/3 -right-40 h-[560px] w-[560px] rounded-full bg-nini-surface" />
      <div className="pointer-events-none absolute -bottom-40 left-1/4 h-[420px] w-[420px] rounded-full bg-nini-mint/40" />

      <header className="relative flex justify-center pt-10 md:pt-14"><Logo size="text-7xl md:text-8xl" /></header>

      <main className="relative max-w-6xl mx-auto px-4 pb-16 md:pb-24 mt-8 md:mt-10 grid lg:grid-cols-[1fr_minmax(420px,560px)_1fr] items-center gap-6">
        <div className="hidden lg:flex justify-center">
          <div className="relative h-[520px] w-[380px] rounded-[220px] overflow-hidden ring-8 ring-nini-mint/60 shadow-lift" data-testid="landing-hero-image">
            <img src={IMG.woman} alt="Member using Nini on her phone" className="w-full h-full object-cover" />
          </div>
        </div>

        <section className="card rounded-[32px] shadow-lift overflow-hidden" data-testid="welcome-card">
          <div className="p-7 md:p-10 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-nini-heading tracking-tight" data-testid="landing-hero-title">{t("landing.title")}</h1>
            <p className="text-base md:text-lg text-nini-muted mt-3 max-w-md mx-auto">{t("landing.subtitle")}</p>
            <div className="mt-8 space-y-4">
              <Option icon={Smartphone} label={t("landing.phone")} primary onClick={() => nav("/login?mode=phone")} testId="landing-continue-phone" />
              <Option icon={Mail} label={t("landing.email")} onClick={() => nav("/signup")} testId="landing-continue-email" />
              <Option icon={Fingerprint} label={t("landing.biometrics")} onClick={biometrics} testId="landing-biometrics" />
            </div>
            <div className="flex items-center gap-4 my-7 text-xs tracking-widest text-nini-muted"><span className="h-px flex-1 bg-nini-border" />{t("landing.or")}<span className="h-px flex-1 bg-nini-border" /></div>
            <div className="grid grid-cols-2 gap-3 mt-1" data-testid="landing-auth-actions">
              <Link to="/login" className="btn-primary !py-3 justify-center text-base" data-testid="landing-login-link">{t("landing.login")}</Link>
              <Link to="/signup" className="btn-secondary !py-3 justify-center text-base" data-testid="landing-signup-link">{t("landing.signup")}</Link>
            </div>
            <p className="text-nini-muted text-sm mt-3">{t("landing.haveAccount")} <Link to="/login" className="text-nini-green font-semibold">{t("landing.login")}</Link> · {t("landing.newHere")} <Link to="/signup" className="text-nini-green font-semibold">{t("landing.signup")}</Link></p>
          </div>
          <div className="bg-nini-surface/80 border-t border-nini-border px-4 py-5 flex items-center justify-center divide-x divide-nini-border text-sm sm:text-base whitespace-nowrap">
            <label className="flex items-center gap-2 px-6 text-nini-heading font-medium cursor-pointer relative" data-testid="landing-language">
              <Globe size={22} strokeWidth={1.6} /> {LANGUAGE_NAMES[lang] || "Language"} <ChevronDown size={16} />
              <select aria-label="Language" data-testid="landing-language-select" className="absolute inset-0 opacity-0 cursor-pointer" value={lang} onChange={(e) => { setLang(e.target.value); setLanguage(e.target.value); }}>
                {Object.entries(LANGUAGE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <a href="mailto:help@nini.africa" className="flex items-center gap-2 px-6 text-nini-heading font-medium" data-testid="landing-help"><HelpCircle size={22} strokeWidth={1.6} /> {t("landing.help")}</a>
          </div>
        </section>

        <div className="hidden lg:flex flex-col items-center gap-6 pl-4">
          <Bubble src={IMG.family} icon={Home} className="-translate-x-6" testId="landing-bubble-home" />
          <Bubble src={IMG.travel} icon={Plane} className="translate-x-6" testId="landing-bubble-travel" />
          <Bubble src={IMG.education} icon={GraduationCap} className="-translate-x-2" testId="landing-bubble-education" />
        </div>

        <div className="lg:hidden flex justify-center gap-4 mt-2">
          {[[IMG.family, Home], [IMG.travel, Plane], [IMG.education, GraduationCap]].map(([s, I], i) => <Bubble key={i} src={s} icon={I} size="h-24 w-24" />)}
        </div>
      </main>

      <footer className="relative text-center pb-8 text-nini-muted">{t("landing.poweredBy")} <span className="font-semibold text-nini-heading">Nini</span><div className="mx-auto mt-2 h-1 w-14 rounded-full bg-nini-sage" /></footer>
    </div>
  );
}
